import { Component } from 'react';
import { Contribution } from '../../../../../../../common/model/input/contributions/types';
import { ModelEntityType } from '../../../../../../../common/model/input/types';
import { ModelEntitySearchResponseItem } from '../../../../../../../common/model/search/types';
import { aiName } from '../../../../../../untidy/texts';
import { TextField } from '../../../../../generic/components/inputs/text-field';
import Search from '../../../output/search';
import {
  ContributionModalParagraph,
  ContributionModalTitle,
  Example,
  FormRow,
  FullRow,
  getContributeSearchStyle,
  Label,
} from './common';
import { FormButton } from './form-button';

interface State {
  newProduct?: string;
  closestProduct?: ModelEntitySearchResponseItem;
  comment?: string;
}

interface Props {
  isSubmitting: boolean;
  submitContribution: (options: { target?: string; data: Contribution }) => void;
}

export class NewProductForm extends Component<Props, State> {
  public state: State = {};

  public render() {
    const { isSubmitting } = this.props;
    const { newProduct, comment, closestProduct } = this.state;

    const canSubmit = !!newProduct && !!closestProduct && !!comment;

    return (
      <>
        <ContributionModalTitle>
          You think we should add a new product or service to the model? Great, please share!
        </ContributionModalTitle>
        <ContributionModalParagraph>
          In Upright's taxonomy of products and services, each product with unique impact gets its own product label.
          The Upright team has built the first draft of the taxonomy, but the idea is to gradually improve it by
          utilizing the wisdom of the crowd.
        </ContributionModalParagraph>
        <form
          onSubmit={e => {
            e.preventDefault();
          }}
        >
          <FormRow>
            <TextField
              label={
                <>
                  <Label>Which product or service should be added to the Upright taxonomy?</Label>
                  <Example>Just type in the product label you think we should add</Example>
                </>
              }
              id="new-product-name"
              type="text"
              value={newProduct}
              disabled={isSubmitting}
              onChange={value => {
                this.setState({ newProduct: value });
              }}
            />
          </FormRow>
          <FormRow>
            <FullRow>
              <Label>
                What is its closest substitute or a related product in the current taxonomy? Search and choose!
              </Label>
              <Example>
                Example: You want to add a “green apples”. In this case, the closest substitute currently in the
                taxonomy could be “apples”.
              </Example>
              <Search
                onChange={(option?: ModelEntitySearchResponseItem) => {
                  this.setState({ closestProduct: option });
                }}
                searchTypes={[ModelEntityType.PRODUCT, ModelEntityType.MIXIN]}
                selectedValue={closestProduct}
                styleOverride={getContributeSearchStyle(!!closestProduct)}
              />
            </FullRow>
          </FormRow>
          <FormRow>
            <TextField
              label={
                <>
                  <Label>
                    How is {newProduct ? `${newProduct.toLowerCase()}'s` : "the product's you suggested"} impact
                    different from{' '}
                    {closestProduct && closestProduct.name
                      ? closestProduct.name.toLowerCase()
                      : ' this closest substitute'}
                    ?
                  </Label>
                  <Example>
                    We ask this to make sure {aiName} understands why this new product is needed and models it in the
                    correct way.
                  </Example>
                </>
              }
              id="impact-difference"
              type="textarea"
              value={comment}
              disabled={isSubmitting}
              onChange={value => {
                this.setState({ comment: value });
              }}
            />
          </FormRow>
          <FormRow>
            <FormButton
              type="submit"
              disabled={isSubmitting || !canSubmit}
              onClick={() => {
                if (newProduct && comment && closestProduct) {
                  this.props.submitContribution({
                    data: {
                      type: 'NEW_PRODUCT',
                      comment,
                      closestProduct: closestProduct.id,
                      newProduct,
                    },
                  });
                }
              }}
              style={{ width: '100%' }}
            >
              Submit
            </FormButton>
          </FormRow>
        </form>
      </>
    );
  }
}
